import React, { useState } from 'react';
import useCart from '../../../hooks/cart/useCart';
import useAuth from '../../../hooks/auth/useAuth';
import { FiTrash2 } from 'react-icons/fi';
import Checkout from '../Checkout/Checkout';
import useValidateOrderQuantity from '../../../hooks/cart/useValidateOrderQuantity';

const CartPage = () => {
  const { user } = useAuth();
  const { cart, updateQuantity, removeFromCart, clearCart } = useCart();
  const [showCheckout, setShowCheckout] = useState(false);
  const { validateQuantity, errors } = useValidateOrderQuantity();

  const total = cart.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0
  );

  if (cart.length === 0) {
    return (
      <div className="max-w-3xl mx-auto py-20 px-4 sm:px-6 lg:px-8 text-center">
        <h1 className="text-4xl font-bold mb-4 text-purple-600">Your Cart</h1>
        <p className="text-gray-500 text-lg">Your cart is empty.</p>
      </div>
    );
  }

  if (showCheckout) {
    return <Checkout cart={cart} total={total} userId={user?.uid} />;
  }

  return (
    <div className="max-w-4xl mx-auto py-20 px-4 sm:px-6 lg:px-8">
      <h1 className="text-4xl font-bold mb-10 text-purple-600 text-center">Your Cart</h1>

      <ul className="space-y-6 mb-12">
        {cart.map(item => (
          <li
            key={item.sku}
            className="flex items-center justify-between bg-white rounded-xl shadow-md px-6 py-4"
          >
            <div className="flex items-center gap-4">
              <img
                src={item.imageUrl}
                alt={item.name}
                className="w-20 h-20 object-cover rounded-lg border"
              />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{item.name}</h2>
                <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                <div className="flex items-center gap-2">
                  <label className="text-sm">Qty:</label>
                  <input
                    type="number"
                    min={1}
                    value={item.quantity}
                    onChange={async (e) => {
                      const newQuantity = parseInt(e.target.value);
                      const isValid = await validateQuantity(item.sku, newQuantity);
                      if (isValid) {
                        updateQuantity(item.sku, newQuantity);
                      }
                    }}
                    className="w-16 px-2 py-1 border rounded text-sm"
                  />
                </div>
                {errors[item.sku] && (
                  <p className="mt-2 text-sm text-red-600">{errors[item.sku]}</p>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end">
              <span className="text-lg font-bold text-purple-600">
                ${(item.price * item.quantity).toFixed(2)}
              </span>
              <button
                onClick={() => removeFromCart(item.sku)}
                className="mt-2 text-red-500 hover:text-red-700 transition"
                title="Remove item"
              >
                <FiTrash2 className="w-5 h-5" />
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className="flex justify-between items-center">
        <button
          onClick={clearCart}
          className="px-4 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded transition"
        >
          Clear Cart
        </button>
        <div className="text-right">
          <p className="text-xl font-semibold text-gray-800">
            Total: <span className="text-purple-600">${total.toFixed(2)}</span>
          </p>
          <button
            onClick={() => setShowCheckout(true)}
            className="mt-2 px-6 py-2 bg-purple-600 text-white font-medium rounded hover:bg-purple-700 transition"
          >
            Proceed to Checkout
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartPage;